import { useEffect, useState } from 'react'
import { DEFAULT_INSURANCE, type InsuranceSetting } from '@shared/worker'
import { useStore } from '../../store'
import { cn } from '../../lib/utils'
import { Switch } from './TradingRiskSettings'

const LEVELS = [75, 85, 90, 95] as const
const SIGMAS = [0.5, 1, 2, 3] as const
const CONFIDENCES = [50, 70, 80, 90] as const
type Stall = NonNullable<InsuranceSetting['stall']>

/**
 * Default insurance for new workers — two kinds, both off unless chosen; each worker can take either
 * or both in chat. The stop stays where the user put it unless the break-even step is on.
 */
export function InsuranceSettings() {
  const settings = useStore((s) => s.settings)
  const update = useStore((s) => s.updateSettings)
  const ins: InsuranceSetting = { ...DEFAULT_INSURANCE, ...(settings?.insurance ?? {}) }
  const stall: Stall = { ...DEFAULT_INSURANCE.stall!, ...(ins.stall ?? {}) }
  const [custom, setCustom] = useState(String(ins.level))
  const [minutes, setMinutes] = useState(stall.minutes != null ? String(stall.minutes) : '')
  useEffect(() => setCustom(String(ins.level)), [ins.level])
  useEffect(() => setMinutes(stall.minutes != null ? String(stall.minutes) : ''), [stall.minutes])
  if (!settings) return null
  const save = (patch: Partial<InsuranceSetting>) => void update({ insurance: { ...ins, stall, ...patch } })
  const saveStall = (patch: Partial<Stall>) => save({ stall: { ...stall, ...patch } })
  const saveCustom = () => {
    const n = Math.round(Number(custom))
    if (Number.isFinite(n) && n >= 50 && n <= 99) save({ level: n })
    else setCustom(String(ins.level))
  }
  const saveMinutes = () => {
    const t = minutes.trim()
    if (!t) return saveStall({ minutes: null })
    const n = Math.round(Number(t))
    if (Number.isFinite(n) && n >= 5 && n <= 1950) saveStall({ minutes: n })
    else setMinutes(stall.minutes != null ? String(stall.minutes) : '')
  }
  const seg = (active: boolean) =>
    cn('rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors', active ? 'bg-[var(--color-surface-3)] text-fg' : 'text-fg-subtle hover:text-fg')
  const input = 'w-14 rounded-md border border-[var(--line-soft)] bg-transparent px-2 py-1 text-right text-[12.5px] text-fg tabular-nums'
  return (
    <section id="settings-insurance" className="card p-4">
      <h2 className="text-[14px] font-semibold text-fg">Insurance</h2>
      <p className="mt-0.5 text-[12.5px] text-fg-subtle">
        The default for new workers — change it for any single worker in chat (“sigortasız”, “başa baş %95”, “takılırsa hedefe in”).
        With both off, the stop stays where you put it. Applies to workers drafted from now on.
      </p>

      {/* 1 · break-even step at IBKR */}
      <div className="mt-3 rounded-lg border border-[var(--line-soft)] p-3">
        <div className="flex items-start justify-between gap-3">
          <span className="min-w-0">
            <span className="text-[13px] font-medium text-fg">Break-even step</span>
            <span className="block text-[11.5px] text-fg-faint">
              At N% of the way from a lot’s stop to its target, IBKR moves that stop to its fee-covered break-even. For trades you want certainty on.
            </span>
          </span>
          <Switch on={ins.on} label="Break-even step" onChange={(v) => save({ on: v })} />
        </div>
        <div className={cn('mt-2.5 space-y-2', !ins.on && 'opacity-50')}>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-lg border border-[var(--line-soft)] p-0.5">
              {LEVELS.map((lv) => (
                <button key={lv} disabled={!ins.on} onClick={() => save({ level: lv })} className={seg(ins.level === lv)}>
                  {lv}%
                </button>
              ))}
            </div>
            <label className="flex items-center gap-1.5 text-[12px] text-fg-subtle">
              custom
              <input
                inputMode="numeric"
                disabled={!ins.on}
                value={custom}
                onChange={(e) => setCustom(e.target.value.replace(/[^0-9]/g, '').slice(0, 2))}
                onBlur={saveCustom}
                onKeyDown={(e) => e.key === 'Enter' && saveCustom()}
                className={input}
              />
              %
            </label>
            <div className="inline-flex rounded-lg border border-[var(--line-soft)] p-0.5">
              <button disabled={!ins.on} onClick={() => save({ basis: 'own' })} className={seg(ins.basis !== 'first')}>
                each lot’s target
              </button>
              <button disabled={!ins.on} onClick={() => save({ basis: 'first' })} className={seg(ins.basis === 'first')}>
                TP1 for all
              </button>
            </div>
          </div>
          <label className="flex items-center justify-between gap-3 text-[12px]">
            <span className="text-fg-muted">
              Protect others
              <span className="block text-[11px] text-fg-faint">When a lot takes its profit, the other lots’ stops move to their break-even.</span>
            </span>
            <Switch on={ins.protectOthers} label="Protect others" onChange={(v) => save({ protectOthers: v })} />
          </label>
        </div>
      </div>

      {/* 2 · the user's own: stalls near the target → the target drops to the zone */}
      <div className="mt-2.5 rounded-lg border border-[var(--line-soft)] p-3">
        <div className="flex items-start justify-between gap-3">
          <span className="min-w-0">
            <span className="text-[13px] font-medium text-fg">Stalls near the target → take the zone</span>
            <span className="block text-[11.5px] text-fg-faint">
              The stop never moves. When the price reaches the zone just under a target and hovers there longer than the stock’s own 5-minute noise
              explains, the target drops to the zone and sells there.
            </span>
          </span>
          <Switch on={stall.on} label="Stall insurance" onChange={(v) => saveStall({ on: v })} />
        </div>
        <div className={cn('mt-2.5 space-y-2 text-[12px]', !stall.on && 'opacity-50')}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-fg-muted">Zone</span>
            <div className="inline-flex rounded-lg border border-[var(--line-soft)] p-0.5">
              {SIGMAS.map((k) => (
                <button key={k} disabled={!stall.on} onClick={() => saveStall({ sigmas: k, way: null })} className={seg(stall.way == null && stall.sigmas === k)}>
                  {k}σ
                </button>
              ))}
              <button disabled={!stall.on} onClick={() => saveStall({ way: 95 })} className={seg(stall.way != null)}>
                95% of the way
              </button>
            </div>
            <span className="text-fg-faint">under the target</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-fg-muted">Wait until noise would have reached it with</span>
            <div className="inline-flex rounded-lg border border-[var(--line-soft)] p-0.5">
              {CONFIDENCES.map((p) => (
                <button key={p} disabled={!stall.on} onClick={() => saveStall({ confidence: p, minutes: null })} className={seg(stall.minutes == null && stall.confidence === p)}>
                  {p}%
                </button>
              ))}
            </div>
            <label className="flex items-center gap-1.5 text-fg-subtle">
              or fixed
              <input
                inputMode="numeric"
                disabled={!stall.on}
                placeholder="—"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                onBlur={saveMinutes}
                onKeyDown={(e) => e.key === 'Enter' && saveMinutes()}
                className={input}
              />
              min
            </label>
          </div>
          <p className="text-[11px] text-fg-faint">
            Wait = (distance ÷ (σ·z))² five-minute bars, trading hours only. PAYX TP1 104.20, σ ≈ $0.15: zone 104.05, about 80 minutes at 80%.
            Runs in our engine on IBKR quotes — it needs real-time data to count well.
          </p>
        </div>
      </div>

      <p className="mt-2.5 text-[11.5px] text-fg-faint">
        Tests (2 years of mechanical panic-drop bounces, not your own picks): near-target reversals were rare; the break-even step cut more winners
        than it saved; the stall exit cost ≈ nothing but saved little; on your trade structure both were ≈ neutral.
      </p>
    </section>
  )
}
